import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import configment from '@adc/configment'
import {
  Dropdown,
  Menu,
  Image,
  Button,
  withApplicationTheme,
  ProductLine,
} from '@adc/ui-core'
import classNames from 'classnames/bind'
import { withApplicationContext } from '../auctionApplication/context'
import auctioncomIconLogo from '../../img/auction-logo.svg'
import smartsaleIconLogo from '../../img/smartsale-logo.svg'
import auctioncomLogo from '../../img/logo-light.svg'
import Search from '../search'
import NativeAppBanner from '../nativeAppBanner'
import { parseAnnouncement } from '../announcementBanner'
import BellNotificationsDrawer, {
  BellNotificationPropType,
} from '../bellNotificationsDrawer'
import { LOGOUT_URL, onLogoutHandler } from '../../util/logout'
import MenuItem from './menuItem'
import MobileHeader from './mobileHeader'
import { NewMobileHeader } from './newMobileHeader'
import NavigationDropdown from './navigationDropdown'
import {
  getNavigationMenuItems,
  getVisibleMenuLinks,
  isDealsForYouNavEnabled,
  shouldShowDealsForYouLink,
} from './navigationDropdown/constants'
import {
  generalRoutes,
  aboutUs,
  dealsForYou,
  getSellLinkData,
  getSmartSaleBuyLinkData,
} from './constants/routes'
import LogoHeader from './logoHeader'
import Nudge from './nudge'
import {
  getNewBuyDropdown,
  loggedInDropdown,
  getLearnDropdown,
} from './constants/menus'
import cls from './styles.scss'

const cx = classNames.bind(cls)

const menuPositionRight = { position: 'right' }

const HEADER_BUY_ELMID = 'h_buy_menu'
const HEADER_LEARN_ELMID = 'h_learn_menu'
const HEADER_USER_ELMID = 'h_user_menu'
const BELL_NOTIFICATIONS_ELMID = 'bell_notifications'
const TRANSITION_SCROLL_OFFSET = 90 // roughly, height of desktop header

const LogoutItem = (
  <MenuItem
    {...generalRoutes.logOut}
    onClick={onLogoutHandler}
    className={cx(generalRoutes.logOut.className)}
  />
)

const NotificationBadge = ({ bellNotificationsCount, className, isInMenu }) =>
  bellNotificationsCount !== '' && (
    <span
      className={cx('bell-notification', className)}
      data-elm-id={
        isInMenu ? 'notification_count_menu_item' : 'notification_count'
      }
    >
      {bellNotificationsCount}
    </span>
  )

NotificationBadge.propTypes = {
  bellNotificationsCount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  className: PropTypes.string,
  isInMenu: PropTypes.bool,
}

const formatNotificationsCount = (bellNotifications, unreadCount) => {
  let bellNotificationsCount = unreadCount ?? bellNotifications.length

  if (bellNotificationsCount >= 20) {
    bellNotificationsCount = '20+'
  } else if (bellNotificationsCount === 0) {
    bellNotificationsCount = ''
  }
  return bellNotificationsCount
}

// Gets the position of the element in reference to the DOM
const getTopPosition = elem =>
  elem.getBoundingClientRect().top + window.pageYOffset

class Header extends PureComponent {
  constructor(props) {
    super(props)
    this.buyHeaderRef = React.createRef()
    this.helpHeaderRef = React.createRef()
    this.moreHeaderRef = React.createRef()
    this.userHeaderRef = React.createRef()
    this.learnHeaderRef = React.createRef()
    this.dropDownRefs = [
      this.buyHeaderRef,
      this.helpHeaderRef,
      this.moreHeaderRef,
      this.userHeaderRef,
      this.learnHeaderRef,
    ]
  }

  static propTypes = {
    customLogo: PropTypes.string,
    featureFlags: PropTypes.object,
    isSticky: PropTypes.bool,
    onLoginClick: PropTypes.func,
    onSignupClick: PropTypes.func,
    onChatClick: PropTypes.func,
    showLiveChat: PropTypes.bool,
    user: PropTypes.object,
    showSearch: PropTypes.bool,
    showLinks: PropTypes.bool,
    bellNotifications: PropTypes.arrayOf(BellNotificationPropType),
    onClickBellNotifications: PropTypes.func,
    onCloseBellNotifications: PropTypes.func,
    bellNotificationsOpen: PropTypes.bool,
    // When true, suppress the notification/announcement spacer even if the flag
    // is on — the announcement banner has been dismissed client-side.
    notificationDismissed: PropTypes.bool,
    onClose: PropTypes.func,
    onSearchSubmit: PropTypes.func,
    hidePreferencesTooltip: PropTypes.bool,
    showPurchaseProfileTooltip: PropTypes.bool,
    onDrawerFetchMore: PropTypes.func,
    unreadCount: PropTypes.number,
    transitionElmId: PropTypes.string,
    filters: PropTypes.object,
    recognizedLocations: PropTypes.array,
    theme: PropTypes.object,
    dropdownStyle: PropTypes.oneOf(['primary-button', 'default-button']),
    className: PropTypes.string,
    forceSmartSaleLogo: PropTypes.bool,
    style: PropTypes.object,
    applicationContext: PropTypes.object,
    embeddedContext: PropTypes.string,
    customMobileContent: PropTypes.node,
    newMobileHeaderLogoVariant: PropTypes.oneOf(['icon', 'full']),
    newMobileHeaderHideShadow: PropTypes.bool,
    showMobileLoginButton: PropTypes.bool,
    enableNewMobileHeader: PropTypes.bool, // Some apps may want to keep using original header (like SRP) even if the flag is enabled
    showDealsForYouIndicator: PropTypes.bool,
    // ISO date string of when the deals-for-you recommendation set was last
    // updated (from graph). Used to hide the direct nav link once the set is
    // stale (older than PRP_NAV_MAX_AGE_DAYS). Undefined => not gated.
    dealsForYouUpdatedDate: PropTypes.string,
    // Origin to prefix root-relative links with (e.g. "https://www.auction.com").
    // Used when the header renders on a reverse-proxied host so links don't
    // resolve against the proxy origin and 302 incorrectly. Empty = relative.
    baseUrl: PropTypes.string,
  }

  static defaultProps = {
    featureFlags: {},
    isSticky: true,
    onChatClick: _.noop,
    onLoginClick: _.noop,
    onSignupClick: _.noop,
    showLiveChat: false,
    user: { logged_in: false },
    showSearch: true,
    showLinks: true,
    onClose: _.noop,
    hidePreferencesTooltip: false,
    showPurchaseProfileTooltip: false,
    bellNotifications: [],
    onClickBellNotifications: _.noop,
    onCloseBellNotifications: _.noop,
    bellNotificationsOpen: false,
    transitionElmId: '',
    theme: {},
    forceSmartSaleLogo: false,
    dropdownStyle: undefined,
    style: {},
    embeddedContext: '',
    customMobileContent: null,
    newMobileHeaderLogoVariant: 'icon',
    showDealsForYouIndicator: false,
    dealsForYouUpdatedDate: undefined,
    baseUrl: '',
  }

  state = {
    notifications: [],
    bellNotificationsOpen: false,
    showSearchOverride: null,
    isClient: false,
  }

  onCloseBellNotifications = () => {
    this.setState({
      bellNotificationsOpen: false,
    })
  }

  onClickBellNotifications = () => {
    this.setState({
      bellNotificationsOpen: true,
    })
  }

  // Normalized names of the dropdown/account-menu links currently shown.
  // Sent as `visibleDropdownLinks` on navigation_click so every nav click
  // records the dropdown links present at the time (empty when the new-nav
  // dropdown isn't rendered). Uses empty handlers — item ids/titles (and thus
  // link names) don't depend on handlers.
  getVisibleDropdownLinks = () => {
    const { featureFlags, theme, user, baseUrl } = this.props
    if (!featureFlags['fe.resi.enable-new-navigation'] || !user.logged_in) {
      return []
    }
    return getVisibleMenuLinks(
      getNavigationMenuItems(featureFlags, {}, true, theme, {
        user,
        showDealsForYouIndicator: this.props.showDealsForYouIndicator,
        dealsForYouUpdatedDate: this.props.dealsForYouUpdatedDate,
        baseUrl,
      })
    )
  }

  renderAuthActions = (visibleHeaderLinks = []) => {
    const {
      user,
      featureFlags,
      hidePreferencesTooltip,
      showPurchaseProfileTooltip,
      onClickBellNotifications,
      bellNotifications,
      unreadCount,
      dropdownStyle: dropdownStylePassedIn,
      showLinks,
      theme,
      baseUrl,
    } = this.props

    const visibleDropdownLinks = this.getVisibleDropdownLinks()
    const headerTrackingContext = {
      linkSource: 'header',
      visibleHeaderLinks,
      visibleDropdownLinks,
    }

    const dropdownStyle =
      dropdownStylePassedIn ||
      (theme.productLine === ProductLine.SmartSale
        ? 'default-button'
        : 'primary-button')

    // Is this the price we pay for seamless toolkit updates?
    const bellNotificationsCount = formatNotificationsCount(
      bellNotifications,
      unreadCount
    )

    const notificationsElement = {
      className: 'notifications',
      elmId: 'h_not',
      trackingName: 'notifications',
      title: (
        <div>
          <NotificationBadge
            bellNotificationsCount={bellNotificationsCount}
            isInMenu
          />{' '}
          Notifications
        </div>
      ),
      onClick: onClickBellNotifications,
    }

    if (
      featureFlags['fe.resi.enable-new-navigation'] &&
      this.props.user.logged_in
    ) {
      const menuItems = getNavigationMenuItems(
        featureFlags,
        {
          onSignOut: onLogoutHandler,
          onNotifications: onClickBellNotifications,
        },
        true, // always true since we're in the logged-in branch
        theme,
        {
          user: this.props.user,
          showDealsForYouIndicator: this.props.showDealsForYouIndicator,
          dealsForYouUpdatedDate: this.props.dealsForYouUpdatedDate,
          baseUrl,
        }
      )

      const NavigationDropdownElement = (
        <NavigationDropdown
          user={user}
          menuItems={menuItems}
          bellNotificationsCount={bellNotificationsCount}
          dropdownStyle={dropdownStyle}
          elmId={HEADER_USER_ELMID}
          indicatorsEnabled={isDealsForYouNavEnabled(featureFlags)}
          showIndicator={
            !!bellNotificationsCount || this.props.showDealsForYouIndicator
          }
          visibleHeaderLinks={visibleHeaderLinks}
        />
      )

      return (
        <Nudge
          user={user}
          elmId='header_nudge'
          showPreferencesTooltip={!hidePreferencesTooltip}
          showPurchaseProfileTooltip={showPurchaseProfileTooltip}
        >
          {NavigationDropdownElement}
        </Nudge>
      )
    }

    if (this.props.user.logged_in) {
      const loggedInDropdownOptions = [
        notificationsElement,
        ...loggedInDropdown(true, featureFlags),
      ]
      const ProfileDropdown = (
        <HeaderDropdown
          buttonStyle={dropdownStyle}
          onDropDownClick={this.onDropDownClick}
          dropdownList={loggedInDropdownOptions}
          text={
            <span className={cx('header-truncate')}>
              {theme.productLine !== ProductLine.SmartSale && (
                <NotificationBadge
                  bellNotificationsCount={bellNotificationsCount}
                  className={cx('top-left')}
                />
              )}{' '}
              Welcome, {user.first_name}
            </span>
          }
          elmId={HEADER_USER_ELMID}
          dropDownRef={this.userHeaderRef}
          direction='left'
        >
          {LogoutItem}
        </HeaderDropdown>
      )
      return (
        <Nudge
          user={user}
          elmId='header_nudge'
          showPreferencesTooltip={!hidePreferencesTooltip}
          showPurchaseProfileTooltip={showPurchaseProfileTooltip}
        >
          {ProfileDropdown}
        </Nudge>
      )
    } else {
      return (
        <Menu.Menu {...(!showLinks ? menuPositionRight : {})}>
          <MenuItem
            {...generalRoutes.signUp}
            baseUrl={baseUrl}
            onClick={this.props.onSignupClick}
            trackingContext={headerTrackingContext}
          />
          <MenuItem
            {...generalRoutes.logIn}
            baseUrl={baseUrl}
            onClick={this.props.onLoginClick}
            trackingContext={headerTrackingContext}
          >
            <Button role='secondary'>Log In</Button>
          </MenuItem>
        </Menu.Menu>
      )
    }
  }

  // react-semantic has an issue with dropdowns in the menu
  // multiple synthetic events are fired that toggle the active state of a dropdown in a menu
  // while a fix is still worked on we need to manually reset the state
  // hack fix shamelessy inspired from here detailing the defects.
  // https://github.com/Semantic-Org/Semantic-UI-React/issues/2046
  onDropDownClick = elmId => {
    const refsToClose = _.filter(this.dropDownRefs, dropDownRef => {
      return (
        dropDownRef.current &&
        dropDownRef.current.props['data-elm-id'] !== elmId
      )
    })
    _.each(refsToClose, dropDownRef => {
      dropDownRef.current && dropDownRef.current.close()
    })
  }

  /**
   * Sets showSearchOverride based on whether user has scrolled past
   * the indicated transition element.
   */
  scrollEvent = () => {
    const delta =
      getTopPosition(this.state.transitionElem) -
      window.pageYOffset -
      TRANSITION_SCROLL_OFFSET

    if (delta < 0) {
      if (!this.state.showSearchOverride) {
        this.setState({ showSearchOverride: true })
      }
    } else {
      if (this.state.showSearchOverride) {
        this.setState({ showSearchOverride: false })
      }
    }
  }

  /**
   * Adds an event listener relative to elem indicated with transitionElmId
   */
  addScrollListener() {
    const { transitionElmId } = this.props
    if (transitionElmId) {
      const transitionElem = document.querySelector(
        `[data-elm-id="${transitionElmId}"]`
      )
      if (transitionElem) {
        this.setState({ transitionElem })
        window.addEventListener('scroll', this.scrollEvent)
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { showSearch } = this.props

    if (prevProps.showSearch !== showSearch) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        showSearchOverride: showSearch,
      })

      // only need listener when initially not showing search (like Home page)
      if (!showSearch) {
        this.addScrollListener()
      } else {
        window.removeEventListener('scroll', this.scrollEvent)
      }
    }
  }

  componentDidMount() {
    this.setState({
      showSearchOverride: this.props.showSearch,
      isClient: true,
    })

    if (!this.props.showSearch) this.addScrollListener()
  }

  // SSR-safe staleness input for the Deals For You link. The recommendation-set
  // updated date is client-only (fetched after mount by usePrpCategoryIndicator),
  // so until the client has mounted we return undefined — which the link gate
  // treats as "not stale" (flag-only). This makes the first client render match
  // the server HTML, then the real staleness gate applies post-mount. Prevents a
  // hydration mismatch from the link showing on the server but hidden on the
  // client (or vice versa).
  // eslint-disable-next-line camelcase
  UNSAFE_componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollEvent)
  }

  renderAuctionComLinks = () => {
    const { featureFlags, theme, baseUrl } = this.props
    const sellLink = getSellLinkData(theme, featureFlags, baseUrl)
    const isDealsForYouLinkShown = shouldShowDealsForYouLink(
      featureFlags,
      this.props.dealsForYouUpdatedDate
    )
    const dealsForYouLinkShown =
      isDealsForYouLinkShown && this.props.user.logged_in
    // Full set of visible top-level header nav links (render order), sent as
    // `visibleHeaderLinks` on navigation_click so clicks can be correlated
    // against whether e.g. the Deals for You link was present.
    const visibleHeaderLinks = [
      'buy',
      typeof sellLink.title === 'string'
        ? sellLink.title.toLowerCase()
        : 'sell',
      'learn',
      ...(dealsForYouLinkShown ? ['deals for you'] : []),
      ...(!isDealsForYouLinkShown ? ['about us'] : []),
    ]
    // Dropdown/account-menu links shown alongside the header links.
    const visibleDropdownLinks = this.getVisibleDropdownLinks()
    const headerTrackingContext = {
      linkSource: 'header',
      visibleHeaderLinks,
      visibleDropdownLinks,
    }
    return (
      <>
        <Menu.Menu {...menuPositionRight}>
          <HeaderDropdown
            onDropDownClick={this.onDropDownClick}
            dropdownList={getNewBuyDropdown(featureFlags)}
            text='Buy'
            direction='left'
            elmId={HEADER_BUY_ELMID}
            dropDownRef={this.buyHeaderRef}
            className='buy-dropdown'
          />
          <MenuItem
            {...sellLink}
            baseUrl={baseUrl}
            className={[sellLink.className]}
            trackingContext={headerTrackingContext}
          />
          <HeaderDropdown
            onDropDownClick={this.onDropDownClick}
            dropdownList={getLearnDropdown(
              featureFlags,
              isDealsForYouLinkShown
            )}
            text='Learn'
            direction='left'
            elmId={HEADER_LEARN_ELMID}
            dropDownRef={this.learnHeaderRef}
            className='learn-dropdown'
          />
          {dealsForYouLinkShown && (
            <MenuItem
              {...dealsForYou}
              baseUrl={baseUrl}
              className={[dealsForYou.className]}
              trackingName='deals for you'
              trackingContext={headerTrackingContext}
              title={
                <span className={cx('nav-link-with-indicator')}>
                  {dealsForYou.title}
                  {this.props.showDealsForYouIndicator && (
                    <span
                      className={cx('nav-link-indicator-dot')}
                      data-elm-id='h_deals_for_you_indicator'
                    />
                  )}
                </span>
              }
            />
          )}
        </Menu.Menu>
        {!isDealsForYouLinkShown && (
          <MenuItem
            {...aboutUs}
            baseUrl={baseUrl}
            className={[aboutUs.className]}
            trackingContext={headerTrackingContext}
          />
        )}
        {this.renderAuthActions(visibleHeaderLinks)}
      </>
    )
  }

  renderSmartSaleLinks = () => {
    const { theme, featureFlags, baseUrl } = this.props
    const buyLink = getSmartSaleBuyLinkData()
    const sellLink = getSellLinkData(theme, featureFlags, baseUrl)
    const visibleHeaderLinks = [
      typeof buyLink.title === 'string' ? buyLink.title.toLowerCase() : 'buy',
      typeof sellLink.title === 'string'
        ? sellLink.title.toLowerCase()
        : 'sell',
    ]
    const visibleDropdownLinks = this.getVisibleDropdownLinks()
    const headerTrackingContext = {
      linkSource: 'header',
      visibleHeaderLinks,
      visibleDropdownLinks,
    }
    return (
      <>
        <Menu.Menu {...menuPositionRight}>
          <MenuItem
            {...buyLink}
            className={[buyLink.className]}
            trackingContext={headerTrackingContext}
          />
          <MenuItem
            {...sellLink}
            baseUrl={baseUrl}
            className={[sellLink.className]}
            trackingContext={headerTrackingContext}
          />
        </Menu.Menu>
        {this.renderAuthActions(visibleHeaderLinks)}
      </>
    )
  }

  renderLinks = () => {
    // only render this client-side due to some client-side logic in the links
    if (!this.state.isClient) return null
    return this.props.theme.productLine === ProductLine.SmartSale
      ? this.renderSmartSaleLinks()
      : this.renderAuctionComLinks()
  }

  render() {
    const { theme } = this.props
    const {
      customLogo,
      featureFlags,
      isSticky,
      onSearchSubmit,
      showSearch,
      showLinks,
      user,
      bellNotifications,
      onClickBellNotifications,
      onCloseBellNotifications,
      bellNotificationsOpen,
      onDrawerFetchMore,
      unreadCount,
      filters,
      recognizedLocations,
      className,
      forceSmartSaleLogo,
      style,
      embeddedContext: embeddedContextInjected,
      applicationContext,
      baseUrl,
    } = this.props

    const { embeddedContext: embeddedContextFromAppContext } =
      applicationContext
    const embeddedContext =
      embeddedContextInjected || embeddedContextFromAppContext

    const { showSearchOverride } = this.state
    const showSearchOnDesktop =
      showSearchOverride !== null ? showSearchOverride : showSearch
    const bellNotificationsCount = formatNotificationsCount(
      bellNotifications,
      unreadCount
    )

    let defaultLogoImg
    if (theme.productLine === ProductLine.SmartSale || forceSmartSaleLogo) {
      const smartsalePoweredLogo = configment.get(
        'SMARTSALE_POWERED_LOGO_URL',
        ''
      )
      defaultLogoImg = showSearchOnDesktop
        ? smartsaleIconLogo
        : smartsalePoweredLogo
    } else {
      defaultLogoImg = showSearchOnDesktop ? auctioncomIconLogo : auctioncomLogo
    }

    const getDefaultIcon = (iconOnly = false) => (
      <Image
        className={cx('icon', { 'icon-only': iconOnly })}
        src={customLogo || defaultLogoImg}
        data-elm-id='h_logo'
        alt='logo'
      />
    )

    /**
     * In a ideal world, the header would just be told if it has to worry about adjusting for notification, but
     * as we are using this as a part of custom component outside the shell and passed through, we need header
     * to determine itself if it needs to account for hasNotification.
     */
    const hasNotification =
      !this.props.notificationDismissed &&
      !!parseAnnouncement(featureFlags['fe.resi.announcement-banner'])

    const useNewMobileHeader =
      this.props.enableNewMobileHeader &&
      featureFlags['fe.resi.enable-new-navigation']

    return embeddedContext === 'webview' ? null : (
      <header style={style}>
        <div
          data-elm-id='header'
          className={cx(
            'header',
            {
              'sticky-header': isSticky,
              'with-notification': hasNotification,
            },
            className
          )}
        >
          {featureFlags['fe.resi.enable-native-app-banner'] && (
            <NativeAppBanner track={_.noop} isSticky={isSticky} />
          )}
          {useNewMobileHeader ? (
            <NewMobileHeader
              logoVariant={this.props.newMobileHeaderLogoVariant}
              showMobileLoginButton={this.props.showMobileLoginButton}
              user={user}
              featureFlags={featureFlags}
              baseUrl={baseUrl}
              onLoginClick={this.props.onLoginClick}
              onSignupClick={this.props.onSignupClick}
              onLogoutClick={onLogoutHandler}
              onClickBell={onClickBellNotifications}
              bellNotificationsCount={String(bellNotificationsCount)}
              forceSmartSaleLogo={forceSmartSaleLogo}
              theme={theme}
              hideShadow={this.props.newMobileHeaderHideShadow}
              showDealsForYouIndicator={this.props.showDealsForYouIndicator}
              dealsForYouUpdatedDate={this.props.dealsForYouUpdatedDate}
            >
              {this.props.customMobileContent}
            </NewMobileHeader>
          ) : (
            <MobileHeader
              featureFlags={featureFlags}
              liveChat={false}
              onLoginClick={this.props.onLoginClick}
              onLogoutClick={onLogoutHandler}
              onSignupClick={this.props.onSignupClick}
              user={user}
              showSearch={showSearch}
              showLinks={showLinks}
              baseUrl={baseUrl}
              hasNotification={hasNotification}
              onSearchSubmit={onSearchSubmit}
              bellNotifications={bellNotifications}
              onClickBell={onClickBellNotifications}
              bellNotificationsCount={String(bellNotificationsCount)}
              filters={filters}
              recognizedLocations={recognizedLocations}
              forceSmartSaleLogo={forceSmartSaleLogo}
              customMobileContent={this.props.customMobileContent}
              showDealsForYouIndicator={this.props.showDealsForYouIndicator}
              dealsForYouUpdatedDate={this.props.dealsForYouUpdatedDate}
              /**
                Normally, one wouldn't need to pass this through as the context should provide it, however, that doesn't
                work very well for WebComponents, in which case we can't rely on the context to provide the theme.
              */
              theme={theme}
            />
          )}

          <div className={cx('menu')}>
            <Menu className={cx('menu-inner')} borderless size='large' icon>
              <MenuItem url='/' baseUrl={baseUrl}>
                {getDefaultIcon(showSearchOnDesktop)}
              </MenuItem>
              {showSearchOnDesktop && (
                <Menu.Item className={cx('search')}>
                  <Search
                    elmId='desktop'
                    filters={filters}
                    recognizedLocations={recognizedLocations}
                    onSearchSubmit={onSearchSubmit}
                    user={user}
                    categorizeResults
                  />
                </Menu.Item>
              )}
              {showLinks ? (
                this.renderLinks()
              ) : (
                <Menu.Menu {...menuPositionRight}>
                  {this.renderAuthActions()}
                </Menu.Menu>
              )}
            </Menu>
          </div>
        </div>
        {isSticky && (
          <div
            className={cx('sticky-header-spacer', {
              'with-notification': !!hasNotification,
              'new-mobile-header': useNewMobileHeader,
            })}
          />
        )}
        <BellNotificationsDrawer
          isOpen={bellNotificationsOpen}
          onClose={onCloseBellNotifications}
          notifications={bellNotifications}
          elmId={BELL_NOTIFICATIONS_ELMID}
          onScrollEnd={onDrawerFetchMore}
        />
      </header>
    )
  }
}

export const HeaderDropdown = React.memo(props => {
  const onDropDownClick = () => {
    return props.onDropDownClick(props.elmId)
  }

  const {
    dropdownList,
    text,
    direction,
    className,
    elmId,
    dropDownRef,
    children,
    buttonStyle = 'menu-item',
  } = props

  return (
    <Dropdown
      onClick={onDropDownClick}
      ref={dropDownRef}
      item
      simple
      text={text}
      direction={direction}
      className={cx(className, buttonStyle)}
      data-elm-id={elmId}
      aria-label='dropdown'
    >
      <Dropdown.Menu>
        {dropdownList.map((item, index) => (
          <MenuItem key={index} role='option' {...item} />
        ))}
        {children}
      </Dropdown.Menu>
    </Dropdown>
  )
})

HeaderDropdown.propTypes = {
  text: PropTypes.node.isRequired,
  dropdownList: PropTypes.array.isRequired,
  direction: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  elmId: PropTypes.string,
  onDropDownClick: PropTypes.func,
  dropDownRef: PropTypes.object,
  buttonStyle: PropTypes.oneOf([
    'primary-button',
    'default-button',
    'menu-item',
  ]),
}

const HeaderWithTheme = withApplicationTheme(withApplicationContext(Header))

HeaderWithTheme.LogoHeader = LogoHeader

export default HeaderWithTheme
export { LOGOUT_URL }
